import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Modal } from 'react-bootstrap';
import { Eye, BoxArrowUpRight, XLg, CheckLg } from 'react-bootstrap-icons';
import AdminLayout, { isStaff } from '../AdminLayout';
import { useSelector } from 'react-redux';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../../common/ui/StateBlock';
import notify, { currentLanguage } from '../../../common/ui/notify';
import { display, formatDateTime } from '../../../common/format';
import './AdminAuction.css';

const L = { geo: 0, en: 1, ru: 2, am: 3 };
const t = (geo, en, ru, am) => [geo, en, ru, am][L[currentLanguage()] ?? 0];

const TABS = ['pending', 'approved', 'rejected', 'all'];
const STATUS_TONE = { pending: 'cx-badge-new', approved: 'cx-badge-sold', rejected: 'cx-badge-danger' };

const AdminAuctionKyc = () => {
    const user = useSelector((s) => s.user);
    const [status, setStatus] = useState('pending');
    const [rows, setRows] = useState([]);
    const [state, setState] = useState('loading');
    const [active, setActive] = useState(null);
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        setState('loading');
        axios.get(`/api/auction/admin/kyc?status=${status}`)
            .then((res) => { setRows(res.data.documents || []); setState('ready'); })
            .catch(() => setState('error'));
    }, [status]);

    useEffect(() => { if (isStaff(user)) load(); }, [load, user]);

    const review = async (decision) => {
        if (!active) return;
        setBusy(true);
        try {
            await axios.post(`/api/auction/admin/kyc/${active.id}/review`, { decision, note });
            notify.success(decision === 'approved'
                ? t('KYC დადასტურდა', 'KYC approved', 'KYC одобрен', 'KYC հաստատվեց')
                : t('KYC უარყოფილია', 'KYC rejected', 'KYC отклонён', 'KYC մերժվեց'));
            setActive(null); setNote('');
            load();
        } catch (err) { notify.apiError(err); }
        finally { setBusy(false); }
    };

    const fileUrl = (id) => `/api/auction/admin/kyc/${id}/file`;

    const body = () => {
        if (state === 'loading') return <LoadingBlock label={t('იტვირთება...', 'Loading...', 'Загрузка...', 'Բեռնվում է...')} />;
        if (state === 'error') return <ErrorBlock title={t('შეცდომა', 'Error', 'Ошибка', 'Սխալ')} retryLabel={t('თავიდან', 'Retry', 'Повтор', 'Կրկին')} onRetry={load} />;
        if (!rows.length) return <EmptyBlock title={t('ჩანაწერი არ არის', 'Nothing here', 'Ничего нет', 'Ոչինչ չկա')} />;
        return (
            <div className="admin-table-wrapper">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>{t('განმცხადებელი', 'Applicant', 'Заявитель', 'Դիմող')}</th>
                            <th className="admin-hide-sm">{t('კონტაქტი', 'Contact', 'Контакт', 'Կոնտակտ')}</th>
                            <th>{t('ტიპი', 'Type', 'Тип', 'Տեսակ')}</th>
                            <th>{t('სტატუსი', 'Status', 'Статус', 'Կարգավիճակ')}</th>
                            <th className="admin-hide-md">{t('თარიღი', 'Date', 'Дата', 'Ամսաթիվ')}</th>
                            <th className="admin-table-actions-col"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id}>
                                <td>
                                    <span className="admin-table-primary">{[r.name, r.surname].filter(Boolean).join(' ') || `#${r.user_id}`}</span>
                                    <span className="admin-table-secondary admin-show-sm">{display(r.phone)}</span>
                                </td>
                                <td className="admin-hide-sm">
                                    <div className="admin-contact-cell">
                                        {r.email && <span>{r.email}</span>}
                                        {r.phone && <span>{r.phone}</span>}
                                    </div>
                                </td>
                                <td>{display(r.doc_type)}</td>
                                <td><span className={`cx-badge ${STATUS_TONE[r.status] || 'cx-badge-neutral'}`}>{r.status}</span></td>
                                <td className="admin-hide-md admin-table-secondary">{formatDateTime(r.created_at)}</td>
                                <td>
                                    <div className="admin-row-actions">
                                        <a className="admin-icon-button" href={fileUrl(r.id)} target="_blank" rel="noreferrer" title={t('დოკუმენტი', 'Document', 'Документ', 'Փաստաթուղթ')}>
                                            <BoxArrowUpRight aria-hidden="true" />
                                        </a>
                                        <button className="admin-icon-button" title={t('განხილვა', 'Review', 'Проверить', 'Ստուգել')}
                                            onClick={() => { setActive(r); setNote(r.review_note || ''); }}>
                                            <Eye aria-hidden="true" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <AdminLayout title={t('KYC განხილვა', 'KYC review', 'Проверка KYC', 'KYC ստուգում')}
            subtitle={t('ბიდერების პირადობის დოკუმენტები', 'Bidder identity documents', 'Документы бидеров', 'Բիդերների փաստաթղթեր')}>
            <div className="admin-status-tabs" role="tablist">
                {TABS.map((tab) => (
                    <button key={tab} type="button" role="tab" aria-selected={status === tab}
                        className={`admin-status-tab ${status === tab ? 'is-active' : ''}`} onClick={() => setStatus(tab)}>
                        {tab}
                    </button>
                ))}
            </div>

            {body()}

            <Modal show={Boolean(active)} centered onHide={() => setActive(null)} contentClassName="cx-dark-modal">
                {active && (
                    <>
                        <Modal.Header>
                            <Modal.Title>{t('KYC განხილვა', 'KYC review', 'Проверка KYC', 'KYC ստուգում')} #{active.id}</Modal.Title>
                            <button type="button" className="admin-modal-close" onClick={() => setActive(null)}><XLg /></button>
                        </Modal.Header>
                        <Modal.Body className="admin-modal-body">
                            <dl className="admin-detail-grid">
                                <div className="admin-detail-row"><dt>{t('სახელი', 'Name', 'Имя', 'Անուն')}</dt><dd>{[active.name, active.surname].filter(Boolean).join(' ')}</dd></div>
                                <div className="admin-detail-row"><dt>Email</dt><dd>{display(active.email)}</dd></div>
                                <div className="admin-detail-row"><dt>{t('ტელეფონი', 'Phone', 'Телефон', 'Հեռախոս')}</dt><dd>{display(active.phone)}</dd></div>
                                <div className="admin-detail-row"><dt>{t('ტიპი', 'Type', 'Тип', 'Տեսակ')}</dt><dd>{display(active.doc_type)}</dd></div>
                            </dl>
                            <a className="cx-btn cx-btn-subtle" href={fileUrl(active.id)} target="_blank" rel="noreferrer">
                                <BoxArrowUpRight /> {t('დოკუმენტის ნახვა', 'View document', 'Открыть документ', 'Բացել փաստաթուղթը')}
                            </a>
                            <div className="admin-detail-block">
                                <h4>{t('შენიშვნა', 'Note', 'Заметка', 'Նշում')}</h4>
                                <textarea className="cx-textarea" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                                    placeholder={t('არასავალდებულო', 'Optional', 'Необязательно', 'Ըստ ցանկության')} />
                            </div>
                        </Modal.Body>
                        <Modal.Footer className="admin-modal-footer">
                            <div className="admin-modal-actions">
                                <button className="cx-btn cx-btn-ghost" disabled={busy} onClick={() => review('rejected')}>
                                    <XLg /> {t('უარყოფა', 'Reject', 'Отклонить', 'Մերժել')}
                                </button>
                                <button className="cx-btn cx-btn-primary" disabled={busy} onClick={() => review('approved')}>
                                    <CheckLg /> {t('დადასტურება', 'Approve', 'Одобрить', 'Հաստատել')}
                                </button>
                            </div>
                        </Modal.Footer>
                    </>
                )}
            </Modal>
        </AdminLayout>
    );
};

export default AdminAuctionKyc;
